import classNames from "classnames";
import { useFormikContext } from "formik";
import moment from "moment";
import { Col, Form, Row } from "react-bootstrap";

import { FileForStatus, GenderOptions } from "../../../../config/filingConstants";
import { IFormITR1 } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import {
  CodePhoneInputField,
  DateTimePicker,
  FormField,
  NumericInputField,
  RadioButtonGroup,
  SelectInputField,
  TextInputField
} from "../../../fields";

type Props = {};

const PermanentInfo: React.FC<Props> = (props: Props) => {
  const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IFormITR1>();

  return (
    <>
      <hr />
      <section className="mb-3">
        <p className="leading-para fw-bold text-primary">
          Provide all info as per your government identity documents (PAN, Aadhaar etc.)
        </p>

        <FormField label="Name" isRequired={true}>
          <>
            <Row className="align-items-start">
              <Col sm={4}>
                <TextInputField
                  placeholder="John"
                  name="FirstName"
                  label="First Name"
                  value={values.FirstName}
                  onChange={(value) => setFieldValue("FirstName", value)}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("FirstName", values.FirstName.trim());
                  }}
                  isInvalid={Boolean(errors.FirstName) && touched.FirstName}
                  errorMsg={errors.FirstName}
                />
              </Col>
              <Col sm={4}>
                <TextInputField
                  placeholder="John"
                  name="MiddleName"
                  label="Middle Name"
                  value={values.MiddleName}
                  onChange={(value) => setFieldValue("MiddleName", value)}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("MiddleName", values.MiddleName.trim());
                  }}
                  isInvalid={Boolean(errors.MiddleName) && touched.MiddleName}
                  errorMsg={errors.MiddleName}
                />
              </Col>
              <Col sm={4}>
                <TextInputField
                  placeholder="John"
                  name="SurNameOrOrgName"
                  label="Last Name"
                  value={values.SurNameOrOrgName}
                  onChange={(value) => setFieldValue("SurNameOrOrgName", value)}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("SurNameOrOrgName", values.SurNameOrOrgName.trim());
                  }}
                  isInvalid={Boolean(errors.SurNameOrOrgName) && touched.SurNameOrOrgName}
                  errorMsg={errors.SurNameOrOrgName}
                />
              </Col>
            </Row>
            <Row
              className={classNames(
                (Boolean(errors.FirstName) && touched.FirstName) ||
                  (Boolean(errors.MiddleName) && touched.MiddleName) ||
                  (Boolean(errors.SurNameOrOrgName) && touched.SurNameOrOrgName)
                  ? "mb-4 mt-n2"
                  : "mt-n4 mb-4"
              )}
            >
              <Col sm={12}>
                <Form.Text className="field-text text-greyDark m-0">
                  Name should be as per the PAN; 5th character of PAN no. is the first letter of the
                  last name
                </Form.Text>
              </Col>
            </Row>
          </>
        </FormField>

        <FormField label="Date of birth / Formation (DD/MM/YYYY)" isRequired={true}>
          <DateTimePicker
            name="DOB"
            value={values.DOB ? moment(values.DOB).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("DOB", _date);
              } else {
                setFieldValue("DOB", "");
              }
            }}
            onBlur={handleBlur("DOB")}
            minDate={undefined}
            maxDate={new Date()}
            isInvalid={Boolean(errors.DOB) && touched.DOB}
            errorMsg={errors.DOB}
            isClearable={true}
          />
        </FormField>

        <FormField label="Date of commencement of Business (DD/MM/YYYY)">
          <DateTimePicker
            name="DOCommencement"
            value={values.DOCommencement ? moment(values.DOCommencement).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("DOCommencement", _date);
              } else {
                setFieldValue("DOCommencement", "");
              }
            }}
            onBlur={handleBlur("DOCommencement")}
            minDate={undefined}
            maxDate={new Date()}
            isInvalid={Boolean(errors.DOCommencement) && touched.DOCommencement}
            errorMsg={errors.DOCommencement}
            isClearable={true}
          />
        </FormField>

        <FormField label="Father's Name" isRequired={true}>
          <TextInputField
            placeholder=""
            name="FatherName"
            value={values.FatherName}
            onChange={(value) => setFieldValue("FatherName", value)}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("FatherName", values.FatherName.trim());
            }}
            isInvalid={Boolean(errors.FatherName) && touched.FatherName}
            errorMsg={errors.FatherName}
          />
        </FormField>

        <FormField label="Gender" isRequired={true}>
          <RadioButtonGroup
            name="Gender"
            value={values.Gender}
            onChange={(selectedType) => setFieldValue("Gender", selectedType)}
            options={UtilService.convertToArray(GenderOptions)}
          />
        </FormField>

        {/* <FormField label="Marital Status" isRequired={true}>
          <SelectInputField
            placeholder="Select status"
            name="MaritalStatus"
            menuZIndex={9}
            isOptionArrayOfObject={false}
            options={MaritalStatus}
            value={[values.MaritalStatus]}
            onChange={(selectedOption) => setFieldValue("MaritalStatus", selectedOption.value)}
            onBlur={handleBlur("MaritalStatus")}
            isInvalid={Boolean(errors.MaritalStatus) && touched.MaritalStatus}
            errorMsg={errors.MaritalStatus}
          />
        </FormField> */}

        <FormField label="Status" isRequired={true}>
          <SelectInputField
            placeholder="Select status"
            name="Status"
            menuZIndex={9}
            isOptionArrayOfObject={false}
            options={FileForStatus}
            value={[values.Status]}
            onChange={(selectedOption) => setFieldValue("Status", selectedOption.value)}
            onBlur={handleBlur("Status")}
            isInvalid={Boolean(errors.Status) && touched.Status}
            errorMsg={errors.Status}
            isDisabled={true}
          />
        </FormField>
      </section>
      <section className="mb-3">
        <p className="leading-para fw-bold text-primary">Identification & Contact details</p>
        <FormField label="Aadhaar Details" isRequired={true}>
          <>
            <Row className="align-items-start">
              <Col>
                <NumericInputField
                  placeholder="Enter 12 digit number"
                  maxLength={12}
                  label="Aadhaar Number"
                  name="AadhaarCardNo"
                  value={values.AadhaarCardNo}
                  onChange={(value) => setFieldValue("AadhaarCardNo", value)}
                  onBlur={handleBlur("AadhaarCardNo")}
                  isInvalid={Boolean(errors.AadhaarCardNo) && touched.AadhaarCardNo}
                  errorMsg={errors.AadhaarCardNo}
                  isDisabled={Boolean(values.AadhaarEnrolmentId)}
                  separator=""
                />
              </Col>
              <Col sm={"auto"} className="px-sm-0 text-center">
                <Form.Label className="mb-0 mt-sm-5">OR</Form.Label>
              </Col>
              <Col>
                <NumericInputField
                  placeholder="Enter 28 digit number"
                  maxLength={28}
                  label="Aadhaar Enrolment No."
                  name="AadhaarEnrolmentId"
                  value={values.AadhaarEnrolmentId}
                  onChange={(value) => setFieldValue("AadhaarEnrolmentId", value)}
                  onBlur={handleBlur("AadhaarEnrolmentId")}
                  isInvalid={Boolean(errors.AadhaarEnrolmentId) && touched.AadhaarEnrolmentId}
                  errorMsg={errors.AadhaarEnrolmentId}
                  isDisabled={Boolean(values.AadhaarCardNo)}
                  separator=""
                />
              </Col>
            </Row>
            <Row
              className={classNames(
                (Boolean(errors.AadhaarCardNo) && touched.AadhaarCardNo) ||
                  (Boolean(errors.AadhaarEnrolmentId) && touched.AadhaarEnrolmentId)
                  ? "mb-4 mt-n2"
                  : "mt-n4 mb-4"
              )}
            >
              <Col sm={12}>
                <Form.Text className="field-text text-greyDark mt-0 mb-1">
                  Please enter either your 12-digit Aadhaar Number or 28-digit Aadhaar Enrolment
                  Number.
                </Form.Text>
                <Form.Text className="field-text text-greyDark m-0">
                  Don't remember your Aadhaar number or Enrolment number?{" "}
                  <a
                    href="https://myaadhaar.uidai.gov.in/retrieve-eid-uid"
                    target="_blank"
                    className="text-secondary fw-bold text-decoration-none"
                    rel="noreferrer"
                  >
                    Search it here
                  </a>
                </Form.Text>
              </Col>
            </Row>
          </>
        </FormField>

        <FormField label="PAN" isRequired={true}>
          <TextInputField
            placeholder="Enter 10 digit PAN"
            name="PAN"
            maxLength={10}
            value={values.PAN}
            onChange={(value) => setFieldValue("PAN", value.toUpperCase())}
            onBlur={handleBlur("PAN")}
            isInvalid={Boolean(errors.PAN) && touched.PAN}
            errorMsg={errors.PAN}
          />
        </FormField>
        <FormField label="Mobile Number" isRequired={true}>
          <CodePhoneInputField
            placeholder="x xxx xxxx xxx"
            maxLength={10}
            name="MobileNo"
            value={values.MobileNo}
            onChange={(value) => {
              setFieldValue("MobileNo", value);
            }}
            onBlur={handleBlur("MobileNo")}
            isInvalid={Boolean(errors.MobileNo) && touched.MobileNo}
            errorMsg={errors.MobileNo}
            countryCodeValue={values.CountryCodeMobile || "+91"}
            onChangeCountryCode={(e) => {
              setFieldValue("CountryCodeMobile", `+${e.value}`);
            }}
            countryCodeOnBlur={handleBlur("CountryCodeMobile")}
            countryCodeName="CountryCodeMobile"
            countyCodeIsInvalid={Boolean(errors.CountryCodeMobile) && touched.CountryCodeMobile}
            countryCodeErrorMsg={errors.CountryCodeMobile}
          />
        </FormField>

        <FormField label="Email" isRequired={true}>
          <TextInputField
            placeholder="email@gmail.com"
            name="EmailAddress"
            value={values.EmailAddress}
            onChange={(value) => setFieldValue("EmailAddress", value)}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("EmailAddress", values.EmailAddress.trim());
            }}
            isInvalid={Boolean(errors.EmailAddress) && touched.EmailAddress}
            errorMsg={errors.EmailAddress}
          />
        </FormField>
      </section>
      <section className="mb-0">
        <p className="leading-para fw-bold text-primary">Optional information</p>

        <FormField label="Secondary Mobile Number">
          <CodePhoneInputField
            placeholder="x xxx xxxx xxx"
            maxLength={10}
            name="MobileNoSec"
            value={values.MobileNoSec}
            onChange={(value) => {
              setFieldValue("MobileNoSec", value);
            }}
            onBlur={handleBlur("MobileNoSec")}
            isInvalid={Boolean(errors.MobileNoSec) && touched.MobileNoSec}
            errorMsg={errors.MobileNoSec}
            countryCodeValue={values.CountryCodeMobileNoSec || "+91"}
            onChangeCountryCode={(e) => {
              setFieldValue("CountryCodeMobileNoSec", `+${e.value}`);
            }}
            countryCodeOnBlur={handleBlur("CountryCodeMobileNoSec")}
            countryCodeName="CountryCodeMobileNoSec"
            countyCodeIsInvalid={
              Boolean(errors.CountryCodeMobileNoSec) && touched.CountryCodeMobileNoSec
            }
            countryCodeErrorMsg={errors.CountryCodeMobileNoSec}
          />
        </FormField>

        <FormField label="Secondary Email">
          <TextInputField
            placeholder="email@gmail.com"
            name="EmailAddressSec"
            value={values.EmailAddressSec}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("EmailAddressSec", values.EmailAddressSec.trim());
            }}
            onChange={(value) => setFieldValue("EmailAddressSec", value)}
            isInvalid={Boolean(errors.EmailAddressSec) && touched.EmailAddressSec}
            errorMsg={errors.EmailAddress}
          />
        </FormField>

        <FormField label="Landline Number">
          <>
            <Row className="align-items-start">
              <Col sm={4}>
                <NumericInputField
                  name="STDCodeLandlineNumber"
                  maxLength={5}
                  label="STD Code"
                  value={values.STDCodeLandlineNumber}
                  onChange={(value) => setFieldValue("STDCodeLandlineNumber", value)}
                  onBlur={handleBlur("STDCodeLandlineNumber")}
                  isInvalid={Boolean(errors.STDCodeLandlineNumber) && touched.STDCodeLandlineNumber}
                  errorMsg={errors.STDCodeLandlineNumber}
                  separator=""
                />
              </Col>
              <Col sm={8}>
                <NumericInputField
                  name="LandlineNumber"
                  maxLength={10}
                  label="Landline Number"
                  value={values.LandlineNumber}
                  onChange={(value) => setFieldValue("LandlineNumber", value)}
                  onBlur={handleBlur("LandlineNumber")}
                  isInvalid={Boolean(errors.LandlineNumber) && touched.LandlineNumber}
                  errorMsg={errors.LandlineNumber}
                  separator=""
                />
              </Col>
            </Row>
          </>
        </FormField>
      </section>
    </>
  );
};

export default PermanentInfo;
